import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../common/Button';
import Modal from '../../common/Modal';
import CardPriceSearch from './CardPriceSearch';
import { setToastsError, setToastsSuccess } from '../../../redux/actions/common';
import { useDispatch, useSelector } from 'react-redux';
import { ChangeRequestControllerService } from '../../../services';
import { setLoader } from '../../../redux/actions/api';
import ChangeRequestBase from './ChangeRequestBase';
import { getRequestingTable } from '../../tables/table';
import { discountValidation, validateFormInput } from '../../../utils/helpers/validation';
import { setDeleteConfirm, setIsContinue, setIsDeleteContinue } from '../../../redux/actions/modal';
import '../../../assets/css/pages/stores/change-request.css';
import '../../../assets/css/pages/stores/change-request-remand.css';
import apiErrorHandler, { apiValidationErrorHandler } from '../../../api/apiErrorHandler';
import { useLocation } from 'react-router-dom';
import { clearErrors } from '../../../redux/actions/error';

const ChangeRequest = (props) => {
    const { confirmStatus, confirmRequestId } = props;

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const location = useLocation();

    const storage = useSelector((state) => state.storage);
    const { isCompetitor, isFreshness } = storage;

    const [state, setState] = useState({
        apiResponse: null,
        reload: new Date(),
        isShowModalEdit: false, // 編集モーダル
        isShowModalApplication: false, // 自店定番売価モーダル
        edlpData: null,
        modalTitle: null,
        editedId: '',
        isShowModalAllSelect: false,
    });

    const [selectedData, setSelectedData] = useState([]);

    const { apiResponse, reload, isShowModalEdit, isShowModalApplication, edlpData, modalTitle, editedId, isShowModalAllSelect } = state;

    const modal = useSelector((state) => state.modal);
    const { isContinue, isDeleteContinue } = modal;

    const setApiResponse = (response) => {
        setState((prevState) => ({
            ...prevState,
            apiResponse: response,
        }));
    };

    const [formData, setFormData] = useState(null);

    const handleEditClick = (index, value) => {
        setFormData(value);
        handleChangeState('isShowModalEdit', true);
    };

    const table = getRequestingTable(t, handleEditClick, confirmStatus === 'REQUESTING', isFreshness, isCompetitor);

    const getSelectedData = () => {
        return apiResponse?.data.filter((item) => item.selected) || [];
    };

    const handleChangeState = (field, value) => {
        setState((prevState) => ({
            ...prevState,
            [field]: value,
        }));
    };

    const handleSubmitEditFormData = async (step = 0) => {
        dispatch(clearErrors());
        handleChangeState('modalTitle', 2);
        // 入力値チェック
        if (validateFormInput(dispatch, formData, isFreshness)) return;

        const discountedRequestSellingPrice = parseInt(formData.discountedRequestSellingPrice);
        const requestData = [
            {
                ...formData,
                discountedRequestSellingPrice,
            },
        ];

        const body = {
            discountedRequestSellingPrice: discountedRequestSellingPrice,
            salesStartDate: formData.salesStartDate,
            salesEndDate: formData.salesEndDate,
            otherSpecialReports: formData.otherSpecialReports,
        };

        try {
            await ChangeRequestControllerService.validateUpdateChangeRequest(formData.id, body);
        } catch (error) {
            apiValidationErrorHandler(dispatch, error, requestData);
            return;
        }

        // 乖離を無視する場合があるため予めセットしておく
        setState((prevState) => ({
            ...prevState,
            requestData: requestData,
        }));

        // 乖離チェック
        if (await discountValidation(dispatch, requestData, isFreshness)) {
            return;
        }

        submitEdit();
    };

    useEffect(() => {
        dispatch(clearErrors());
        if (isContinue) {
            // 乖離モーダル表示後に処理を続行する場合
            submitEdit();
            dispatch(setIsContinue(false));
        }
        if (isDeleteContinue) {
            // 削除確認モーダル表示後に処理を続行する場合
            handleCheckDataDeleteSubmit();
            dispatch(setIsDeleteContinue(false));
        }
    }, [dispatch, isContinue, isDeleteContinue, location]);

    const submitEdit = () => {
        dispatch(setLoader(true));
        dispatch(clearErrors());
        const request = isCompetitor
            ? ChangeRequestControllerService.updateChangeRequestCompetitor(formData.id, formData)
            : isFreshness
              ? ChangeRequestControllerService.updateChangeRequestFreshness(formData.id, formData)
              : ChangeRequestControllerService.updateChangeRequestInstruction(formData.id, formData);

        request
            .then(() => {
                dispatch(setToastsSuccess('編集が完了しました。'));
                handleChangeState('isShowModalEdit', false);
                handleChangeState('reload', new Date());
                handleChangeState('editedId', formData.id);
            })
            .catch((error) => {
                apiValidationErrorHandler(dispatch, error, formData, t('message.error.update'));
            })
            .finally(() => {
                dispatch(setLoader(false));
            });
    };

    const handleCheckDataDeleteSubmit = () => {
        dispatch(clearErrors());
        const ids = selectedData.map((item) => item.id);
        ChangeRequestControllerService.deleteChangeRequest(ids)
            .then(() => {
                dispatch(setToastsSuccess('削除が完了しました。'));
                handleChangeState('reload', new Date());
            })
            .catch((error) => {
                apiErrorHandler(dispatch, error, t('message.error.delete'));
            })
            .finally(() => {
                handleChangeState('isShowDeleteConfirmModal', false);
            });
    };

    const handleShowSubmitModal = async (step = 0) => {
        dispatch(clearErrors());
        dispatch(setLoader(true));
        handleChangeState('modalTitle', 1);
        if (selectedData.length > 0) {
            for (const item of selectedData) {
                try {
                    await ChangeRequestControllerService.validateUpdateChangeRequest(item.id);
                } catch (error) {
                    dispatch(setLoader(false));
                    apiValidationErrorHandler(dispatch, error, [{ ...item }]);
                    return;
                }
            }
        }
        handleSubmit(selectedData);
    };

    const handleSubmitModal = () => {
        dispatch(clearErrors());
        handleSubmit(selectedData);
    };

    const handleSubmit = async (data) => {
        if (data.length > 0) {
            const ids = data.map((item) => item.id);
            ChangeRequestControllerService.apply(ids)
                .then(() => {
                    dispatch(setLoader(false));
                    dispatch(setToastsSuccess('申請しました。承認画面よりご確認ください。'));
                    handleChangeState('isShowModalApplication', false);
                    handleChangeState('reload', new Date());
                })
                .catch((error) => {
                    apiValidationErrorHandler(dispatch, error, data, t('message.error.update'));
                });
        } else {
            dispatch(setLoader(false));
            handleChangeState('isShowModalAllSelect', true);
        }
    };

    const handleShowDeleteModal = () => {
        showDeleteModal(selectedData);
    };

    const showDeleteModal = (selectedData) => {
        if (selectedData.length > 0) {
            dispatch(setDeleteConfirm(true));
        } else {
            dispatch(setToastsError('削除するデータを選択してください。'));
        }
    };

    const clearEditedId = () => {
        handleChangeState('editedId', '');
    };

    return (
        <>
            <ChangeRequestBase
                table={table}
                confirmStatus={confirmStatus}
                apiResponse={apiResponse}
                setApiResponse={setApiResponse}
                reload={reload}
                confirmRequestId={confirmRequestId}
                editedId={editedId}
                clearEditedId={clearEditedId}
                selectedData={selectedData}
                setSelectedData={setSelectedData}
            />

            <div className="d-flex justify-content-end c-slide-top mb-5">
                <Button
                    id="submit-button"
                    className="btn btn--primary mr-2"
                    onClick={handleShowSubmitModal}
                    disabled={apiResponse?.data.length === 0}
                    children={<span className="btn-text">{t('price_request.apply_button')}</span>}
                />
                <Button
                    id="delete-button"
                    className="btn btn--primary"
                    onClick={handleShowDeleteModal}
                    disabled={apiResponse?.data.length === 0}
                    children={<span className="btn-text">{t('price_request.delete_button')}</span>}
                />
            </div>
            <Modal
                isShow={isShowModalEdit}
                onClose={() => handleChangeState('isShowModalEdit', false)}
                isShowIconClose="true"
                title={t('card_registration.title')}
                className="registration-edit-modal"
                btnList={[
                    {
                        name: t('button.update'),
                        className: 'btn--primary',
                        onClick: () => handleSubmitEditFormData(),
                        disabled: false,
                    },
                    {
                        name: t('button.cancel'),
                        className: 'btn--secondary',
                        onClick: () => handleChangeState('isShowModalEdit', false),
                        disabled: false,
                    },
                ]}
                children={
                    <CardPriceSearch
                        formData={formData}
                        setFormData={setFormData}
                        taxRate={formData?.taxRate}
                        capacityOfUnit={formData?.capacityOfUnit}
                        quantityOfUnit={formData?.quantityOfUnit}
                        discountedLowestSellingPrice={formData?.discountedLowestSellingPrice}
                    />
                }
            />
            <Modal
                size="sm"
                isShow={isShowModalAllSelect}
                onClose={() => handleChangeState('isShowModalAllSelect', false)}
                isShowIconClose="true"
                title={t('message.info.please.select.data.to.request.title')}
                btnList={[
                    {
                        name: t('button.OK'),
                        className: 'btn--primary',
                        onClick: () => handleChangeState('isShowModalAllSelect', false),
                        disabled: false,
                    },
                ]}
                children={
                    <>
                        <p class="text-center">{t('message.info.please.select.data.to.request.request')}</p>
                    </>
                }
            />
        </>
    );
};

export default ChangeRequest;
